import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamiliesData } from './useFamiliesData';
import { HearthCard } from './HearthCard';
import { IncomingHearthInvites } from './IncomingHearthInvites';

/* v9 4.1 — Hearths tab list.
   Shows a summary strip and a stack of HearthCards.

   Stage-2 banner: a transient blue "Stage 2 of 2 — tap a Hearth to connect
   it to WiFi" sits above the list only on the single arrival from Stage-1
   setup. The signal is a sessionStorage flag (higlo.showSetupStep2Banner)
   set by App.handleSetupComplete(). First tap on any Hearth clears the
   flag, and the banner never reappears this session — even navigating back
   to this tab or reloading won't bring it back. */
export function HearthsTab({ familyIDs, owningFamilies, primaryFamilyID, accountUUID, userEmail }) {
  const navigate = useNavigate();
  const families = useFamiliesData(familyIDs);

  // Read the session flag on mount. Use state so the banner disappears
  // synchronously on Hearth tap without waiting for a rerender from route
  // navigation.
  const [showSetupBanner, setShowSetupBanner] = useState(() => {
    try {
      return sessionStorage.getItem('higlo.showSetupStep2Banner') === '1';
    } catch (_) {
      return false;
    }
  });

  // If the flag was already cleared in another tab / by another component,
  // sync on mount. We only clear ourselves, never re-raise.
  useEffect(() => {
    if (!showSetupBanner) return;
    try {
      if (sessionStorage.getItem('higlo.showSetupStep2Banner') !== '1') {
        setShowSetupBanner(false);
      }
    } catch (_) {}
  }, [showSetupBanner]);

  let ready = 0;
  let offline = 0;
  let pending = 0;
  for (const fid of familyIDs) {
    const f = families[fid] || {};
    if (f.hasEverSignedIn === false) pending += 1;
    else if (!f.isOnline) offline += 1;
    else ready += 1;
  }

  const summary = [
    `${familyIDs.length} Hearth${familyIDs.length === 1 ? '' : 's'}`,
    ready ? `${ready} ready` : null,
    offline ? `${offline} offline` : null,
    pending ? `${pending} not yet connected` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (familyIDs.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-[13px] text-slate-500">
          You don&rsquo;t have any Hearths yet.
        </p>
      </div>
    );
  }

  const clearSetupBanner = () => {
    if (!showSetupBanner) return;
    setShowSetupBanner(false);
    try {
      sessionStorage.removeItem('higlo.showSetupStep2Banner');
    } catch (_) {}
  };

  const onCardClick = (fid) => {
    // Always go to the Hearth detail page — the yellow "Ready to setup?" card
    // on the detail page will offer Setup if the device hasn't phoned home
    // yet. This matches the user's expectation that tapping a Hearth shows
    // the Hearth's page, not the setup flow directly.
    clearSetupBanner();
    navigate(`/app/hearths/${fid}`);
  };

  return (
    <div className="px-4 pt-3 pb-6">
      {showSetupBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 mb-3 flex items-start gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 mt-0.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div className="text-[12px] leading-snug">
            <span className="font-semibold text-blue-900">Stage 2 of 2 — </span>
            <span className="text-blue-800">
              tap a Hearth to connect it to WiFi.
            </span>
          </div>
        </div>
      )}
      <IncomingHearthInvites userEmail={userEmail} accountUUID={accountUUID} />

      <div className="text-[13px] text-slate-500 mb-4">{summary}</div>
      <div className="space-y-3">
        {familyIDs.map((fid) => (
          <HearthCard
            key={fid}
            family={families[fid]}
            isPrimary={fid === primaryFamilyID}
            isOwner={owningFamilies.includes(fid)}
            onClick={() => onCardClick(fid)}
          />
        ))}
      </div>
    </div>
  );
}
